import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api from '@/lib/axios';
import { monthIsoBounds, monthLabel, shiftYearMonth } from '@/lib/gkz';
import { setFooterSettings } from '@/components/FooterDateBar';
import { toast } from 'sonner';
import { statusLabel } from '@/lib/deLabels';

export default function GkzAuswertungPage() {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [groups, setGroups] = useState([]);
  const [codes, setCodes] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [gkz, setGkz] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/gkz-groups').then((r) => {
      const list = r.data || [];
      setGroups(list);
      if (list.length && !groupId) setGroupId(list[0].id);
    }).catch(() => setGroups([]));
    api.get('/gkz-groups/codes').then((r) => setCodes(r.data || [])).catch(() => setCodes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyZeitreise = (y, m, gid) => {
    const { von, bis } = monthIsoBounds(y, m);
    setFooterSettings({
      zeitraumOn: true,
      von,
      bis,
      stichtagOn: true,
      stichtag: von,
      nurAktive: false,
      gkzGroupId: gid || '',
      gkz: gkz && !gid ? gkz : '',
    });
  };

  const loadReport = async (y = year, m = month, gid = groupId, code = gkz) => {
    if (!gid && !code) {
      setReport(null);
      return;
    }
    setLoading(true);
    try {
      const params = { year: y, month: m };
      if (gid) params.group_id = gid;
      if (code && !gid) params.gkz = code;
      const res = await api.get('/gkz-groups/report', { params });
      setReport(res.data);
      applyZeitreise(y, m, gid);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auswertung konnte nicht geladen werden');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (groupId || gkz) loadReport(year, month, groupId, gkz);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, groupId]);

  const goMonth = (delta) => {
    const next = shiftYearMonth(year, month, delta);
    setYear(next.year);
    setMonth(next.month);
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="gkz-auswertung-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">GKZ-Auswertung</h1>
          <p className="text-slate-600 mt-1">
            Monatliche Schülerliste nach Gemeindekennziffer des Zahlungspflichtigen
            bzw. GKZ-Gruppe (z. B. Samtgemeinde Schüttorf).
            Die Zeitreise in der Fußleiste wird auf den gewählten Monat gesetzt.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <p className="text-xs text-slate-500 mb-1">Monat</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => goMonth(-1)} data-testid="gkz-month-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="min-w-[10rem] text-center font-semibold capitalize" data-testid="gkz-month-label">
                {monthLabel(year, month)}
              </span>
              <Button variant="outline" size="sm" onClick={() => goMonth(1)} data-testid="gkz-month-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="min-w-[16rem]">
            <p className="text-xs text-slate-500 mb-1">GKZ-Gruppe</p>
            <Select value={groupId || '__none'} onValueChange={(v) => { setGroupId(v === '__none' ? '' : v); if (v !== '__none') setGkz(''); }}>
              <SelectTrigger data-testid="gkz-group-select">
                <SelectValue placeholder="Gruppe wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Keine Gruppe (einzelne GKZ)</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!groupId && (
            <div className="min-w-[10rem]">
              <p className="text-xs text-slate-500 mb-1">Einzelne GKZ</p>
              <Select value={gkz || '__none'} onValueChange={(v) => { setGkz(v === '__none' ? '' : v); loadReport(year, month, '', v === '__none' ? '' : v); }}>
                <SelectTrigger data-testid="gkz-code-select">
                  <SelectValue placeholder="GKZ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">—</SelectItem>
                  {codes.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Users className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Schüler:innen im Monat</p>
              <p className="text-2xl font-bold" data-testid="gkz-count">{loading ? '…' : (report?.count ?? 0)}</p>
              {report?.group_name && (
                <p className="text-xs text-slate-500">{report.group_name} · {(report.codes || []).join(', ')}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full" data-testid="gkz-students-table">
            <thead className="bg-slate-50 text-slate-500 font-medium text-xs uppercase tracking-wider border-b">
              <tr>
                <th className="p-3 text-left">Schüler:in</th>
                <th className="p-3 text-left">GKZ</th>
                <th className="p-3 text-left">Ort</th>
                <th className="p-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="p-8 text-center text-slate-500">Laden…</td></tr>
              ) : !report || report.students.length === 0 ? (
                <tr><td colSpan={4} className="p-8 text-center text-slate-500" data-testid="gkz-empty">Keine Schüler:innen für diesen Monat</td></tr>
              ) : report.students.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  data-testid={`gkz-student-${s.id}`}
                  onClick={() => navigate(`/students/${s.id}`)}
                >
                  <td className="p-3 text-sm font-medium">{s.last_name}, {s.first_name}</td>
                  <td className="p-3 text-sm">{s.gkz || '—'}</td>
                  <td className="p-3 text-sm">{[s.postal_code, s.city].filter(Boolean).join(' ') || '—'}</td>
                  <td className="p-3 text-sm">{statusLabel(s.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
